import React from 'react';
import LoadingScreen from '../../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import UsagePanel from './UsagePanel';
import moment from 'moment';
import VoxelLink from '../../../components/voxel/Link/VoxelLink';
import { TeamMenuChoices } from '../TeamPage';
import UsageMetric from './UsageMetric';

export interface UsagePageProps {
  team: CurrentTeamGq;
}

export type ReloadFunction = () => Promise<unknown>;

export const UsagePage: React.FC<UsagePageProps> = ({ team }) => {
  if (!team) {
    return <LoadingScreen />;
  }

  return (
    <div className="w-full flex flex-col gap-8">
      <div>
        <VoxelText variant="title2" paddingBot>
          Usage
        </VoxelText>
        <VoxelText variant="bread-normal">
          Your plan includes a limited amount of usage. If the usage exceeds the allotted limit, you
          will need to acquire more capacity. Visit the{' '}
          <VoxelLink
            className="pr-1.5"
            openInNewIcon
            openInNewTab
            href={'/team/' + TeamMenuChoices.Subscription}
          >
            subscription page
          </VoxelLink>
          for more information.
        </VoxelText>
      </div>
      {!!team.activeSubscriptionPeriod && (
        <>
          <VoxelText variant="bread-semibold" color="dark">
            Current period (
            {moment.utc(team.activeSubscriptionPeriod?.publicPeriodStart).format('MMM DD')} -{' '}
            {moment.utc(team.activeSubscriptionPeriod?.publicPeriodEnd).format('MMM DD')})
          </VoxelText>
        </>
      )}
      <div className="w-full flex flex-col">
        <div className="w-full flex flex-col gap-4">
          {/* Team app usage */}
          <div className="grid grid-cols-3 justify-items-stretch gap-8 ">
            <UsagePanel
              title="Search volume"
              blocked={team.blockMonthlyUseSearchVolumeKw}
              tooltip="Number of attempted keywords while collecting Google Ads data (regardless if data was found or not)"
            >
              <UsageMetric
                title="Keywords"
                value={team.activeSubscriptionPeriod?.monthlyUseSearchVolumeKw ?? 0}
                limit={team.activeSubscriptionPeriod?.monthlyCapSearchVolumeKw ?? 0}
                blocked={team.blockMonthlyUseSearchVolumeKw}
                onLimitWarning
              />
            </UsagePanel>
            <UsagePanel
              title="Rank data"
              blocked={team.blockMonthlyUseRankKw}
              tooltip="Number of keyword SERP results (weekly rank tracking included)"
            >
              <UsageMetric
                title="Keywords"
                value={team.activeSubscriptionPeriod?.monthlyUseRankKw ?? 0}
                limit={team.activeSubscriptionPeriod?.monthlyCapRankKw ?? 0}
                blocked={team.blockMonthlyUseRankKw}
                onLimitWarning
              />
            </UsagePanel>
            <UsagePanel
              title="Translations"
              blocked={team.blockMonthlyUseTranslateKw}
              tooltip="Number of translated keywords using the Google Translate API"
            >
              <UsageMetric
                title="Keywords"
                value={team.activeSubscriptionPeriod?.monthlyUseTranslateKw ?? 0}
                limit={team.activeSubscriptionPeriod?.monthlyCapTranslateKw ?? 0}
                blocked={team.blockMonthlyUseTranslateKw}
                onLimitWarning
              />
            </UsagePanel>
            <UsagePanel
              title="ChatGPT"
              blocked={team.blockMonthlyUseChatGptDrafts}
              tooltip="Number of generated texts created by ChatGPT in the content plan section"
            >
              <UsageMetric
                title="Drafts"
                value={team.activeSubscriptionPeriod?.monthlyUseChatGptDrafts ?? 0}
                limit={team.activeSubscriptionPeriod?.monthlyCapChatGptDrafts ?? 0}
                blocked={team.blockMonthlyUseChatGptDrafts}
                onLimitWarning
              />
            </UsagePanel>

            {/* 
            <UsagePanel
              title="Active members"
              blocked={false}
              tooltip="Number of active team members (signed in and not removed)"
            >
              <UsageMetric
                title="Members"
                value={team.activeUsers ?? 0}
                limit={team.licenses ?? 1}
              />
            </UsagePanel>
            */}
            <UsagePanel
              title="Sponsored teams"
              blocked={false}
              tooltip="Number of teams who share their project data with your workspace"
            >
              <UsageMetric
                title="Connections"
                value={team.activeChildConnections ?? 0}
                limit={(team.licenses ?? 0) * 3}
              />
            </UsagePanel>

            {/* Only show the proxy part if the team is blocked - this limit isn't officially communicated */}
            {team.blockMonthlyProxies && (
              <UsagePanel
                title="Proxy usage"
                blocked={team.blockMonthlyProxies}
                tooltip="Proxy calls are used in various Voxel operations. Contact the support team if you are having issues with this."
              >
                <UsageMetric
                  title="Total usage"
                  value={team.activeSubscriptionPeriod?.monthlyCapProxyCalls ?? 0}
                  limit={team.activeSubscriptionPeriod?.monthlyUseProxyCalls ?? 0}
                  blocked={team.blockMonthlyProxies}
                />
              </UsagePanel>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsagePage;
